/** AGENTUL PLANULUI DE SITUAȚIE, partea PURĂ a panoului (`PanouPlanAgent.tsx`):
 *  ce scrie serverul în rezumatul soluției (`summary.agent_plan`) și în firul
 *  rulării (jobul `site_plan_agent`, același endpoint ca firul de fațadă), și
 *  ce arată ecranul din ele — fără rețea, ca s-o poată proba un test.
 *
 *  Agentul rescrie documentul planului LA FIECARE LOT ACCEPTAT: omul vede
 *  planșa crescând. De aceea pasul `lot_scris` e cel după care macheta are ce
 *  reciti, iar rezumatul se citește din registrul viu, nu din rândul cu care
 *  s-a deschis ecranul. */
import type { RandFir } from "../fatada/firAgent";
import type { RegistruSolutii } from "../../nav/api";

/** Felul jobului agentului, cum îl scrie control plane-ul pe rândul rulării. */
export const JOB_AGENT_PLAN = "site_plan_agent";

/** Pașii după care agentul a SCRIS documentul planului. */
export const PASI_CARE_SCRIU_PLAN = new Set(["agent_plan.pas.lot_scris"]);

export type StareAgentPlan = "in_lucru" | "gata" | "limita" | "picat" | "oprit";

/** `summary.agent_plan` al unei soluții de plan de situație (lipsește pe
 *  planurile de dinaintea agentului). Cifrele sunt ale ULTIMEI rulări. */
export interface AgentPlanRezumat {
  status: StareAgentPlan;
  locuri: number | null;
  locuri_tinta: number | null;
  verde_pct: number | null;
  verde_cota_pct: number | null;
  revizie: number | null;
  loturi: { ok: number; respinse: number };
  cost_usd: number | null;
  tururi: number | null;
  /** ce n-a putut rezolva agentul: obiectul (o alee, un loc, o zonă) și de ce */
  blocaje: { obiect: string; motiv: string }[];
  /** când a scris ultima oară (ISO) */
  la: string | null;
}

const STARI: ReadonlySet<string> = new Set(["in_lucru", "gata", "limita", "picat", "oprit"]);

const numarSauNul = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/** Rezumatul agentului din `summary`, cu forma verificată: un rezumat vechi
 *  sau străin (`status` necunoscut) nu ajunge pe ecran ca cifre false. */
export function agentulPlanului(summary: unknown): AgentPlanRezumat | null {
  const a = (summary as { agent_plan?: unknown } | null | undefined)?.agent_plan;
  if (!a || typeof a !== "object") return null;
  const r = a as Record<string, unknown>;
  if (typeof r.status !== "string" || !STARI.has(r.status)) return null;
  const loturi = (r.loturi ?? {}) as Record<string, unknown>;
  const blocaje = Array.isArray(r.blocaje)
    ? r.blocaje
      .filter((b): b is Record<string, unknown> => !!b && typeof b === "object")
      .map((b) => ({ obiect: String(b.obiect ?? ""), motiv: String(b.motiv ?? "") }))
    : [];
  return {
    status: r.status as StareAgentPlan,
    locuri: numarSauNul(r.locuri), locuri_tinta: numarSauNul(r.locuri_tinta),
    verde_pct: numarSauNul(r.verde_pct), verde_cota_pct: numarSauNul(r.verde_cota_pct),
    revizie: numarSauNul(r.revizie),
    loturi: { ok: numarSauNul(loturi.ok) ?? 0, respinse: numarSauNul(loturi.respinse) ?? 0 },
    cost_usd: numarSauNul(r.cost_usd), tururi: numarSauNul(r.tururi),
    blocaje, la: typeof r.la === "string" ? r.la : null,
  };
}

/** Starea de arătat în capul panoului: cât rulează jobul, «lucrează», oricare
 *  ar fi rezumatul (el se rescrie abia la sfârșit); altfel starea rezumatului;
 *  fără niciuna, `null` — agentul n-a lucrat pe planul ăsta. */
export function stareaAgentului(ruleaza: boolean, rezumat: AgentPlanRezumat | null): StareAgentPlan | null {
  if (ruleaza) return "in_lucru";
  return rezumat?.status ?? null;
}

/** Rulează agentul planului pe soluția `planId`, după registrul viu? */
export function ruleazaInRegistru(registru: RegistruSolutii | null | undefined, planId: string | null): boolean {
  if (!registru || !planId) return false;
  return registru.solutii.some((s) => s.id === planId && s.rulare?.kind === JOB_AGENT_PLAN);
}

/** Ultimele `n` propoziții ale agentului pentru om (turii și încheierea), de
 *  la cea mai veche la cea mai nouă — ce îi spune asistentul omului când
 *  întreabă «ce face agentul». */
export function ultimelePropozitii(randuri: readonly RandFir[], n = 5): string[] {
  return randuri.filter((r) => r.fel === "om").slice(-n).map((r) => r.text);
}

/** Câte scrieri pe document a făcut rularea, după firul ei: când numărul
 *  crește pe o rulare văzută rulând, macheta recitește documentul. */
export function scrierileFirului(randuri: readonly RandFir[]): number {
  return randuri.filter((r) => r.cod && PASI_CARE_SCRIU_PLAN.has(r.cod)).length;
}
